import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAccount } from '../../contexts/AccountContext';
import { apiErrorMessage } from '../../utils/apiError';
import mcpServiceService from '../../services/mcpServiceService';
import integrationsService from '../../services/integrationsService';
import Button from '../shared/Button';
import ImplementationBoard from '../shared/ImplementationBoard';

// THE HOTEL'S IMPLEMENTATION — the first tab of /mcp-service/hotels/:hotelId. The pipelines hconnect built for the
// hotel's PMS (V644, Admin › Platform › MCP as a Service › the system) and this hotel's implementation of each: the
// template's board, drawn with this property's credentials (the shared ImplementationBoard, as the template editor's
// Implementations tab draws it).
//
// A template the hotel has no implementation of yet is SET UP here in one click — created as a draft for this hotel
// (its Universal API credential issued by the server), and its board drawn: each credential still needed shows red
// and is set by clicking it. No wizard. Status is the
// template's implementation status (draft / active / paused), on its own endpoint as in the editor; active needs
// every slot filled.
//
// GET /pipelines/mcp-service/hotels/{hotelId}/implementation lists them (gated on the hotel); the board and the status
// go through /pipelines/{id}/…, which let this hotel's users at a template of their PMS (db.js MCP_HOTEL_BRANCH).

export default function McpHotelImplementation({ hotel }) {
  const { isSuperAdmin } = useAuth();
  const { selectedAccount, selectedPartner } = useAccount();
  const [view, setView] = useState(null);
  const [graphs, setGraphs] = useState({});      // templateId -> graph
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const v = await mcpServiceService.getImplementation(hotel.hotelId);
      setView(v);
      // Only the implemented templates are drawn, so only theirs are read.
      const drawn = (v.templates || []).filter(t => t.implementation_id);
      setGraphs(await integrationsService.getBoardGraphs(drawn.map(t => t.id)));
    } catch (e) { setError(apiErrorMessage(e)); }
  }, [hotel.hotelId]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (t, status) => {
    if (status === t.implementation_status) return;
    setBusy(t.id); setError(null);
    try {
      await integrationsService.setImplementationStatus(t.id, t.implementation_id, status);
      setView(v => ({ ...v, templates: v.templates.map(x => (x.id === t.id ? { ...x, implementation_status: status } : x)) }));
    } catch (e) { setError(apiErrorMessage(e, 'The status could not be changed.')); } finally { setBusy(null); }
  };
  const saved = useCallback((t) => (impl) => setView(v => ({ ...v, templates: v.templates.map(x => (x.id === t.id
    ? { ...x, filled: (impl.bindings || []).filter(b => b.credential_id).length } : x)) })), []);
  const boardError = useCallback((msg) => setError(msg), []);
  const setUp = async (t) => {
    setBusy(t.id); setError(null);
    try {
      await integrationsService.saveImplementation(t.id, { hotelId: hotel.hotelId, name: hotel.hotelName, status: 'draft', bindings: [] });
      await load();
    } catch (e) { setError(apiErrorMessage(e, 'This pipeline could not be set up here.')); } finally { setBusy(null); }
  };

  if (error && !view) return <div className="os-msg os-msg-error">{error}</div>;
  if (!view) return <p>Loading…</p>;

  if (!view.pms) {
    return (
      <div className="mcps-card">
        <h3>No PMS chosen</h3>
        <p className="mcps-meta">Choose the hotel&rsquo;s PMS on the Settings tab. Its implementation is built from the pipelines
          made for that PMS.</p>
      </div>
    );
  }

  return (
    <div className="mcps-section">
      {error && <div className="os-msg os-msg-error">{error}</div>}
      <p className="mcps-meta">The pipelines made for <strong>{view.pms.name}</strong>, as {hotel.hotelName} runs them.</p>

      {!view.templates.length && (
        <div className="mcps-card">
          <p className="mcps-meta">No pipelines have been made for {view.pms.name} yet. hconnect builds them under Admin ›
            Platform › MCP as a Service.</p>
        </div>
      )}

      {view.templates.map(t => (
        <div className="mcps-card" key={t.id}>
          <div className="mcps-card-head">
            <h3>{t.name}</h3>
            {t.implementation_id ? (
              <div className="mcps-actions">
                <span className={`mcps-pill ${t.filled < t.slots ? 'mcps-pill-pending' : 'mcps-pill-verified'}`}>
                  {t.filled} of {t.slots} filled</span>
                <select value={t.implementation_status} disabled={!hotel.canEdit || busy === t.id}
                        onChange={e => setStatus(t, e.target.value)} aria-label={`Status of ${t.name}`}>
                  <option value="draft">draft</option>
                  <option value="active" disabled={t.filled < t.slots}>
                    {t.filled < t.slots ? `active (${t.slots - t.filled} slot(s) empty)` : 'active'}</option>
                  <option value="paused">paused</option>
                </select>
              </div>
            ) : (
              <Button variant="primary" disabled={!hotel.canEdit || !t.slots || busy === t.id} onClick={() => setUp(t)}
                      title={!t.slots ? 'This pipeline has nothing to fill yet' : undefined}>Set up</Button>
            )}
          </div>

          {t.implementation_id ? (
            graphs[t.id] ? (
              <ImplementationBoard
                templateId={t.id}
                implementationId={t.implementation_id}
                graph={graphs[t.id]}
                status={t.implementation_status}
                accountId={selectedAccount}
                partnerId={selectedPartner}
                isSuperAdmin={isSuperAdmin}
                onSaved={saved(t)}
                onError={boardError}
              />
            ) : <p className="mcps-meta">This pipeline&rsquo;s design could not be read.</p>
          ) : (
            <p className="mcps-meta">Not set up at this hotel yet. <strong>Set up</strong> adds it; then set each red credential on
              its board.</p>
          )}
        </div>
      ))}
    </div>
  );
}
